import { createContext, type ReactNode, use } from 'react';
import type { RuntimeConfig } from '@/lib/types.ts';

const FALLBACK: RuntimeConfig = { env: 'local' };

/** Fetches `/config.json` (a static file overwritten per deployment) before the app renders. */
export async function loadConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch(new URL('/config.json', window.location.origin), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return FALLBACK;
    const data: unknown = await response.json();
    if (
      typeof data === 'object' &&
      data !== null &&
      typeof (data as RuntimeConfig).env === 'string'
    ) {
      return data as RuntimeConfig;
    }
    return FALLBACK;
  } catch {
    return FALLBACK;
  }
}

const ConfigContext = createContext<RuntimeConfig>(FALLBACK);

export function ConfigProvider({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig(): RuntimeConfig {
  return use(ConfigContext);
}
